import { LIST_SEPARATOR_LAST, LIST_SEPARATOR_MIDDLE, LIST_SEPARATOR_START, LIST_SEPARATOR_TWO } from './constants';
import { lookup } from './lookup';
import { separateAll } from './string';
import type { PhraseMap } from './types';

const DEFAULT_SEPARATOR = ', ';

/**
 * Returns the list of items joined using the appropriate separator. If strings for separators are not found, it joins
 * the list of items using `, `.
 *
 * @param items Items to join.
 */
type Join = <T>(locale: string, phrases: PhraseMap, items: T[]) => (string | T)[];
export const join: Join = (locale, phrases, items) => {
  switch (items.length) {
    case 0:
      return items;
    case 1:
      return items;
    case 2:
      return [items[0], lookup(locale, phrases, LIST_SEPARATOR_TWO) || DEFAULT_SEPARATOR, items[1]];
    case 3:
      return [
        items[0],
        lookup(locale, phrases, LIST_SEPARATOR_START) || DEFAULT_SEPARATOR,
        items[1],
        lookup(locale, phrases, LIST_SEPARATOR_LAST) || DEFAULT_SEPARATOR,
        items[2],
      ];
    default:
      return [
        items[0],
        lookup(locale, phrases, LIST_SEPARATOR_START) || DEFAULT_SEPARATOR,
        ...separateAll(
          items.slice(1, items.length - 1),
          lookup(locale, phrases, LIST_SEPARATOR_MIDDLE) || DEFAULT_SEPARATOR
        ),
        lookup(locale, phrases, LIST_SEPARATOR_LAST) || DEFAULT_SEPARATOR,
        items[items.length - 1],
      ];
  }
};

/**
 * Returns the list of items joined using the comma separator. If strings for separators are not found, it joins
 * the list of items using `, `.
 *
 * @param items Items to join.
 */
type SimpleJoin = <T>(locale: string, phrases: PhraseMap, items: T[]) => (string | T)[];
export const simpleJoin: SimpleJoin = (locale, phrases, items) =>
  separateAll(items, lookup(locale, phrases, LIST_SEPARATOR_MIDDLE) || DEFAULT_SEPARATOR);

/**
 * Convenience helper for joining strings - returns string, so can be used in templates.
 */
type JoinStrings = (locale: string, phrases: PhraseMap, items: string[]) => string;
export const joinStrings: JoinStrings = (locale, phrases, items) => join(locale, phrases, items).join('');

type SimpleJoinStrings = (locale: string, phrases: PhraseMap, items: string[]) => string;
export const simpleJoinStrings: SimpleJoinStrings = (locale, phrases, items) =>
  simpleJoin(locale, phrases, items).join('');
